import {Condition} from '../contracts/report-definition.types';
import {
	ENUMERATED_VALUES_KIND,
	ReportTaxonomy,
	fieldByFilterField,
	operatorLabel,
	resolverKindFor,
} from '../contracts/report-taxonomy.types';
import {requiresEveryValue} from './conditionOperators';
import {enumeratedLabelResolver} from './enumeratedValues';
import {fieldForStoredCondition} from './retiredFilterFields';
import {REMAINING_WORK_YES, remainingWorkSelection} from './remainingWorkCondition';

export type LabelResolver = (value: string) => string | undefined;

export interface SummaryLabels {
	or: string;
	/** Joins the values of an operator that requires them ALL to match. Separate from `or` because
	 * the two are not interchangeable: "Is all A or B" states the opposite of what it filters. */
	and: string;
	selected: (n: number) => string;
	yes: string;
	no: string;
}

const DEFAULT_LABELS: SummaryLabels = {
	or: 'or',
	and: 'and',
	selected: n => `${n} selected`,
	yes: 'Yes',
	no: 'No',
};

export interface FormatConditionSummaryOptions {
	resolveLabel?: LabelResolver;
	labels?: SummaryLabels;
	/** Whether to prefix the summary with the field's name.
	 *
	 * Default true, which is what the chat intent card needs — it renders a condition with no label
	 * beside it. The builder tile passes false: it renders a <Label> with the field name directly
	 * above a 220px trigger, so repeating it there both duplicates the name and spends the width the
	 * values need.
	 *
	 * Only affects shapes that have an actual value summary to shorten. An empty condition has none —
	 * the name IS the whole return value there, not a prefix in front of one — so this option is
	 * ignored for it and the name is always returned. Suppressing it too would hand a caller with no
	 * placeholder of its own (a non-editable field with no stored value) a blank, unlabelled control. */
	includeFieldName?: boolean;
	/** Whether two or more named values summarise as a count ("In 2 selected") rather than a joined
	 * list. Default false.
	 *
	 * The builder tile passes true for the text it SHOWS: its trigger is 220px wide, and two or three
	 * names joined with "or" clip after the first, which reads as a filter on that one value. The
	 * operator is kept, so "In" and "Not in" still read differently. The tile's title and accessible
	 * name are built with `listAll` instead, so the full list is one hover away. The chat intent card
	 * does not clip, and keeps the list.
	 *
	 * Only affects values that are names. A numeric, date or duration comparison is short and has no
	 * name to clip, and keeps its literal values. */
	countMultiple?: boolean;
	/** Whether to list every value by name however many there are. Default false, which counts past
	 * three ("Project: 4 selected").
	 *
	 * The builder tile passes true for its title and accessible name, whose job is to say what the
	 * count on the trigger stands for. A value whose label is not known cannot be listed, so if any
	 * is unresolved this returns the operator count form ("In 4 selected") rather than naming only
	 * some of them. */
	listAll?: boolean;
}

export function formatConditionSummary(
	condition: Condition,
	taxonomy: ReportTaxonomy,
	opts?: FormatConditionSummaryOptions
): string {
	const labels = opts?.labels ?? DEFAULT_LABELS;
	const field = fieldByFilterField(taxonomy, condition.field);
	// Same lookup, same reason as the tile: a condition stored against a retired filter token keeps
	// its human name here too, so the chat intent card cannot name a wire token the reader has never
	// seen in the interface. `field` is untouched - it stays undefined for those, so the branches
	// below go on treating the condition as one whose catalog entry is not available.
	const name = fieldForStoredCondition(taxonomy, condition.field)?.label ?? condition.field;
	const op = operatorLabel(taxonomy, condition.operator);
	const values = condition.values ?? [];
	// Which word joins the values is the OPERATOR's business, not the field's: an all-family
	// operator matches rows carrying every value, so listing them with "or" describes a different,
	// broader filter than the one that will run.
	const conjunction = requiresEveryValue(condition.operator) ? labels.and : labels.or;

	// Every non-empty return below composes from `prefix` rather than `name`, so the option is
	// honoured uniformly across them — the "N selected" fallback included.
	const prefix = opts?.includeFieldName === false ? '' : name;

	// Nothing below this point is needed to name an empty condition, and resolving a kind for one is
	// wasted work on the most common shape there is - a condition the reader has just added. Always
	// `name`, never `prefix`: there is no value summary here for the option to shorten, so honouring
	// it would return an empty string instead of just skipping a prefix - blank and unlabelled for a
	// non-editable field with no stored value, which has no placeholder of its own to fall back to.
	if (values.length === 0) {
		return name;
	}

	const kind = field ? resolverKindFor(taxonomy, field) : undefined;
	// A caller that holds no value source of its own (the chat intent card) still gets the catalog's
	// display form for an enumerated field, so one condition cannot read two ways on two surfaces.
	const resolveLabel =
		opts?.resolveLabel ?? (field && kind === ENUMERATED_VALUES_KIND ? enumeratedLabelResolver(field) : undefined);

	if (field?.dataType === 'boolean') {
		const yes = values[0] === 'true';
		return `${prefix ? `${prefix} ` : ''}${op} ${yes ? labels.yes : labels.no}`;
	}

	// Remaining work is a number field the reader answers Yes or No to, like Bug and Blocked, so its
	// summary has to read that way too - otherwise the tile says "greater than 0" while the control
	// beside it offers Yes and No. Which comparison means which answer is `remainingWorkSelection`'s
	// business, not this function's, so a stored comparison the control cannot express (a
	// "more than 8 hours left" an agent wrote, say) selects nothing here and falls through to the
	// numeric branch below, which still shows the comparison it really is. The operator shown is IS
	// rather than the stored comparison, for the same reason the answer is Yes: "greater than Yes"
	// is not a sentence, and IS is what the equivalent boolean tiles store.
	if (field && kind === 'remainingWork') {
		const [picked] = remainingWorkSelection(condition);
		if (picked !== undefined) {
			const answer = picked === REMAINING_WORK_YES ? labels.yes : labels.no;
			return `${prefix ? `${prefix} ` : ''}${operatorLabel(taxonomy, 'IS')} ${answer}`;
		}
	}

	// Numeric/date/duration fields are never id-backed — the stored values ARE the human-readable
	// values (e.g. "8", "2024-01-01"). Render them directly, before the id-resolve/count logic
	// below, so a comparison condition never collapses to a misleading "N selected".
	//
	// Except when the catalog publishes the field's vocabulary. Progress is declared `number` and
	// yet its stored values are band labels ("51-75") whose display form carries the unit ("51-75%").
	// Rendering it here would show the tile's summary WITHOUT the percent the picker beside it shows
	// WITH it, which reads as two different values for one condition. `enumeratedValues` resolves
	// every declared value and echoes an undeclared one, so the resolve path below cannot collapse
	// this field to "N selected" the way this branch exists to prevent.
	if (
		kind !== ENUMERATED_VALUES_KIND &&
		(field?.dataType === 'number' || field?.dataType === 'date' || field?.dataType === 'duration')
	) {
		if (values.length === 1) {
			return `${prefix ? `${prefix} ` : ''}${op} ${values[0]}`;
		}
		return `${prefix ? `${prefix} ` : ''}${op} ${values.join(` ${conjunction} `)}`;
	}

	const resolved = values.map(v => (resolveLabel ? resolveLabel(v) : v));
	const anyUnresolved = resolved.some(r => r === undefined);

	// Checked before the bare-count fallback below, which drops the operator: with it first, a
	// fourth value (or one whose label has not loaded) turned "Not in 3 selected" into "4 selected",
	// which reads the same as its opposite.
	if (opts?.countMultiple === true && values.length > 1) {
		return `${prefix ? `${prefix} ` : ''}${op} ${labels.selected(values.length)}`;
	}

	if (opts?.listAll === true && anyUnresolved && values.length > 1) {
		return `${prefix ? `${prefix} ` : ''}${op} ${labels.selected(values.length)}`;
	}

	if (anyUnresolved || (values.length > 3 && opts?.listAll !== true)) {
		return prefix ? `${prefix}: ${labels.selected(values.length)}` : labels.selected(values.length);
	}

	const resolvedLabels = resolved as string[];
	if (resolvedLabels.length === 1) {
		return `${prefix ? `${prefix} ` : ''}${op} ${resolvedLabels[0]}`;
	}
	return `${prefix ? `${prefix} ` : ''}${op} ${resolvedLabels.join(` ${conjunction} `)}`;
}
